import {setTitle} from '@github-ui/document-metadata'
import {JobInfoWithSubscription} from '@github-ui/issues-bulk-actions/JobInfoWithSubscription'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {useLocation, useNavigate, useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarPanes} from '@github-ui/repo-issues-page-layout/useRepoIssuesSidebarPanes'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {useUser} from '@github-ui/use-user'
import {AlertIcon, BookmarkSlashIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {renderIssuesSidebar} from '../../components/sidebar/render-issues-sidebar'
import {SavedViewIssueList} from '../../components/views/SavedViewIssueList'
import {LABELS} from '../../constants/labels'
import {PreviewQueryProvider} from '../../contexts/PreviewQueryContext'
import {useQueryBulk} from '../../contexts/QueryContext'
import {
  SAVED_VIEW_OPEN_SOURCES,
  type SavedViewOpenLocationState,
  useSavedViewAnalytics,
} from '../../hooks/use-saved-view-analytics'
import {IssueRepoNewChoosePage, IssueRepoNewPage, IssueShowPage, IssueViewsPage} from '../../lazy-routes'
import {SavedViewFetchError, savedViewQueryOptions} from '../../queries/saved-views'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import styles from './RepositorySavedViewPage.module.css'
import {SavedViewEditView} from './SavedViewEditView'
import {SavedViewFilterBar} from './SavedViewFilterBar'
import {SavedViewHeader} from './SavedViewHeader'

function RepositorySavedViewContent() {
  const {owner = '', repo = '', id = ''} = useParams<{owner: string; repo: string; id: string}>()
  const viewsPath = `/${owner}/${repo}/issues/views`
  const {pathname, search, state} = useLocation()
  const navigate = useNavigate()

  const {data, error, isError, isLoading, refetch} = useQuery(savedViewQueryOptions({owner, repo, id}))

  const savedView = data?.savedView

  const {trackViewOpened, trackEditOpened} = useSavedViewAnalytics()
  const {currentUser} = useUser()
  const openSource = (state as SavedViewOpenLocationState | null)?.savedViewOpenSource ?? SAVED_VIEW_OPEN_SOURCES.direct
  const trackedOpenIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (savedView === undefined) return
    if (trackedOpenIdRef.current === savedView.id) return
    trackedOpenIdRef.current = savedView.id
    const openedByCreator = currentUser?.login != null && currentUser.login === savedView.creatorLogin
    trackViewOpened(savedView.id, openSource, openedByCreator)
  }, [savedView, trackViewOpened, openSource, currentUser])

  useEffect(() => {
    if (savedView?.name) {
      setTitle(LABELS.documentTitleForView(savedView.name))
    }
  }, [savedView?.name])

  const urlQuery = useMemo(() => new URLSearchParams(search).get('q'), [search])
  const effectiveQuery = urlQuery ?? savedView?.query ?? ''

  const updateUrlQuery = useCallback(
    (nextQuery: string) => {
      const trimmed = nextQuery.trim()
      const params = new URLSearchParams(search)
      if (trimmed) {
        params.set('q', trimmed)
      } else {
        params.delete('q')
      }
      // Reset pagination when the query changes; the new result set may be shorter.
      params.delete('page')
      const query = params.toString()
      navigate(query ? `${pathname}?${query}` : pathname)
    },
    [navigate, pathname, search],
  )

  const [isEditing, setIsEditing] = useState(false)

  const handleStartEditing = useCallback(() => {
    if (savedView !== undefined) {
      trackEditOpened(savedView.id)
    }
    setIsEditing(true)
  }, [savedView, trackEditOpened])

  const handleExitEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSaveSuccess = useCallback(() => {
    if (urlQuery !== null) {
      updateUrlQuery('')
    }
    setIsEditing(false)
  }, [updateUrlQuery, urlQuery])

  const readPreviewQueryValue = useMemo(
    () => ({currentQuery: effectiveQuery, onQueryChange: updateUrlQuery}),
    [effectiveQuery, updateUrlQuery],
  )

  const isNotFound = error instanceof SavedViewFetchError && error.status === 404

  const notFoundBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <BookmarkSlashIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>View not found</Blankslate.Heading>
      <Blankslate.Description>It may have been deleted, or the URL may be incorrect.</Blankslate.Description>
      {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
      <Blankslate.PrimaryAction onClick={() => navigate(viewsPath)}>Back to Views</Blankslate.PrimaryAction>
    </Blankslate>
  )

  const errorBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Couldn’t load this view</Blankslate.Heading>
      <Blankslate.Description>Something went wrong loading this view. Please try again.</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => refetch()}>Try again</Blankslate.PrimaryAction>
      {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
      <Blankslate.SecondaryAction href={viewsPath}>Back to Views</Blankslate.SecondaryAction>
    </Blankslate>
  )

  const fetchErrorBlankslate = isNotFound ? notFoundBlankslate : errorBlankslate

  const renderErrorFallback = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Couldn’t display this view</Blankslate.Heading>
      <Blankslate.Description>
        Something went wrong displaying this view. Try reloading the page.
      </Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => window.location.reload()}>Reload page</Blankslate.PrimaryAction>
    </Blankslate>
  )

  return (
    <div className={styles.middlePaneWrapper} data-hpc containertiming="hpc" aria-busy={isLoading}>
      <ErrorBoundary boundaryName="RepositorySavedViewPage" fallback={renderErrorFallback}>
        {isError ? (
          fetchErrorBlankslate
        ) : savedView ? (
          isEditing ? (
            <SavedViewEditView
              key={savedView.id}
              savedView={savedView}
              owner={owner}
              repo={repo}
              onSaved={handleSaveSuccess}
              onCancel={handleExitEditing}
            />
          ) : (
            <>
              <SavedViewHeader
                savedView={savedView}
                owner={owner}
                repo={repo}
                viewsPath={viewsPath}
                onStartEditing={handleStartEditing}
              />
              <SavedViewFilterBar
                owner={owner}
                repo={repo}
                savedView={savedView}
                currentQuery={effectiveQuery}
                onQueryChange={updateUrlQuery}
              />
              <div className={styles.middlePaneGrid}>
                <PreviewQueryProvider value={readPreviewQueryValue}>
                  <SavedViewIssueList owner={owner} repo={repo} query={effectiveQuery} />
                </PreviewQueryProvider>
              </div>
            </>
          )
        ) : null}
      </ErrorBoundary>
    </div>
  )
}

export function RepositorySavedViewPage() {
  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueViewsPage.preload()
      IssueShowPage.preload()
      IssueRepoNewPage.preload()
      IssueRepoNewChoosePage.preload()
    })
  }, [])

  const {bulkJobId, setBulkJobId, triggerBulkJobSuccess} = useQueryBulk()

  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- See above; issue_pinned_views_index_page is similarly actor-aware.
  const indexPageEnabled = useFeatureFlag('issue_pinned_views_index_page')
  const sidebarPanes = useRepoIssuesSidebarPanes(renderIssuesSidebar)
  if (!pinnedViewsEnabled || !indexPageEnabled) {
    return null
  }

  return (
    <AnalyticsWrapper category="Repository Saved View">
      <JobInfoWithSubscription bulkJobId={bulkJobId} setBulkJobId={setBulkJobId} onSuccess={triggerBulkJobSuccess}>
        <ThreePanesLayout
          contentAs="div"
          resizeable={false}
          leftPaneWidth="medium"
          collapsibleLeftPane={sidebarPanes.collapsibleLeftPane}
          middlePane={<RepositorySavedViewContent />}
        />
      </JobInfoWithSubscription>
    </AnalyticsWrapper>
  )
}
